import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { FitBadge, ScoreBars, ScoreRadar } from '../components/hikari/ScoreBreakdown';
import { ShieldCheck, Sparkle, ArrowRight, Eye, SunHorizon } from '@phosphor-icons/react';

const PublicScore = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/public/score/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'revoked' : 'error'));
  }, [token]);

  if (error === 'revoked') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center" data-testid="share-revoked">
          <div className="label-eyebrow text-primary mb-2">LINK REVOKED</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
            This Hikari score is no longer public.
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            The candidate may have turned sharing off. Ask them for a new link.
          </p>
          <Link to="/">
            <Button className="rounded-none">Go to Hikari →</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center mono text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-score-page">
      {/* Slim public header */}
      <header className="border-b border-border">
        <div className="max-w-[1100px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <SunHorizon size={22} weight="duotone" className="text-primary" />
            <span className="font-display font-black text-lg tracking-tight">HIKARI</span>
            <span className="label-eyebrow ml-2 text-muted-foreground hidden sm:inline">HIRING ENGINE</span>
          </Link>
          <Link to="/register?role=employer">
            <Button size="sm" variant="outline" className="rounded-none">I'm hiring →</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-12">
        {/* Hero: candidate + score */}
        <div className="grid lg:grid-cols-12 gap-0 grid-skeleton mb-10">
          <div className="lg:col-span-7 bg-white p-8">
            <div className="label-eyebrow text-primary mb-3">HIKARI PROOF · PUBLIC SCORE</div>
            <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-2">
              {data.display_name}
            </h1>
            <div className="text-sm text-muted-foreground mb-4">
              {data.headline || '—'}
              {data.role_hint && <span className="text-foreground"> · evaluated for <strong>{data.role_hint}</strong></span>}
            </div>
            <div className="mono text-xs text-muted-foreground flex items-center gap-3">
              <span>Generated {new Date(data.generated_at).toLocaleDateString()}</span>
              <span className="flex items-center gap-1"><Eye size={12} /> {data.view_count} views</span>
              <span>{data.attempts_considered} attempts considered</span>
            </div>
          </div>
          <div className="lg:col-span-5 bg-foreground text-background p-8 flex flex-col items-center justify-center">
            <div className="label-eyebrow text-accent mb-2">OVERALL FIT</div>
            <FitBadge value={data.overall_fit} size="lg" />
            <div className="mono text-4xl font-black mt-2">{data.overall_fit}<span className="text-lg opacity-60">/100</span></div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid md:grid-cols-2 gap-0 grid-skeleton mb-10">
          <div className="bg-white p-6">
            <div className="label-eyebrow text-muted-foreground mb-3">RADAR</div>
            <ScoreRadar scores={data} />
          </div>
          <div className="bg-white p-6">
            <div className="label-eyebrow text-muted-foreground mb-3">BREAKDOWN</div>
            <ScoreBars scores={data} />
          </div>
        </div>

        {/* AI explanation */}
        {data.explanation && (
          <div className="bg-white border border-border p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-primary" />
              <div className="label-eyebrow">WHY THIS SCORE</div>
            </div>
            <p className="text-sm leading-relaxed">{data.explanation}</p>
          </div>
        )}

        {data.work_style_summary && (
          <div className="bg-foreground text-background p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={18} className="text-accent" />
              <div className="label-eyebrow text-accent">WORK-STYLE SIGNAL</div>
            </div>
            <p className="font-display text-xl lg:text-2xl leading-snug tracking-tight">
              "{data.work_style_summary}"
            </p>
          </div>
        )}

        {/* Call-to-action for employer */}
        <div className="bg-primary text-primary-foreground p-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="label-eyebrow opacity-80 mb-1">HIRING?</div>
            <div className="font-display font-black text-2xl tracking-tight">
              Get every candidate scored like this.
            </div>
          </div>
          <Link to="/register?role=employer">
            <Button size="lg" className="rounded-none h-12 px-6 bg-background text-foreground hover:bg-background/90" data-testid="public-score-cta-employer">
              Start hiring with Hikari <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>

        <div className="mt-8 text-center mono text-xs text-muted-foreground">
          Hikari scores are generated from live assessments + work evidence — not resumes.{' '}
          <Link to="/engine" className="text-primary hover:underline">See how the engine works →</Link>
        </div>
      </main>
    </div>
  );
};

export default PublicScore;
